import { useEffect } from 'react';

/**
 * SEO Component - Manages document head metadata
 * Uses native DOM manipulation for React 19 compatibility
 * (react-helmet-async v2 has issues with React 19)
 */
const SEO = ({ 
  title = '', 
  description = '', 
  keywords = '',
  image = '',
  url = '',
  type = 'website',
  product = null
}) => {
  const siteTitle = 'Genora Peptides';
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const defaultDescription = 'Premium research-grade peptides for scientific study. Shop Retatrutide, SS-31, GLP-1, GLP-3, AOD-9604, BPC-157, Semaglutide and more. 99%+ purity verified with certificates of analysis.';
  
  // Build the full title - ensure it's always a string
  const finalTitle = title 
    ? `${String(title)} | ${siteTitle}` 
    : `${siteTitle} | Research-Grade Peptides | Retatrutide, SS-31, GLP-1`;
  
  const finalDescription = description ? String(description) : defaultDescription;
  const finalKeywords = keywords ? String(keywords) : 'research peptides, buy peptides, Retatrutide, SS-31, SS31, GLP-1, GLP-3, GLP1, GLP3, AOD-9604, BPC-157, TB-500, Semaglutide, peptide research, high purity peptides, research chemicals, genora peptides';
  const finalImage = image ? String(image) : `${siteUrl}/og-image.png`;
  const finalUrl = url ? String(url) : (typeof window !== 'undefined' ? window.location.href : '');

  useEffect(() => {
    // Update document title
    document.title = finalTitle;
    
    // Helper function to update or create meta tag
    const updateMeta = (selector, content, isProperty = false) => {
      let tag = document.querySelector(selector);
      if (tag) {
        tag.setAttribute('content', content);
      } else {
        tag = document.createElement('meta');
        if (isProperty) {
          tag.setAttribute('property', selector.replace('meta[property="', '').replace('"]', ''));
        } else {
          tag.setAttribute('name', selector.replace('meta[name="', '').replace('"]', ''));
        }
        tag.setAttribute('content', content);
        document.head.appendChild(tag);
      }
    };
    
    // Update primary meta tags
    updateMeta('meta[name="description"]', finalDescription);
    updateMeta('meta[name="keywords"]', finalKeywords);
    
    // Update Open Graph tags
    updateMeta('meta[property="og:type"]', type, true);
    updateMeta('meta[property="og:url"]', finalUrl, true);
    updateMeta('meta[property="og:title"]', finalTitle, true);
    updateMeta('meta[property="og:description"]', finalDescription, true);
    updateMeta('meta[property="og:image"]', finalImage, true);
    updateMeta('meta[property="og:site_name"]', siteTitle, true);
    
    // Update Twitter tags
    updateMeta('meta[name="twitter:card"]', 'summary_large_image');
    updateMeta('meta[name="twitter:url"]', finalUrl);
    updateMeta('meta[name="twitter:title"]', finalTitle);
    updateMeta('meta[name="twitter:description"]', finalDescription);
    updateMeta('meta[name="twitter:image"]', finalImage);
    
    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', finalUrl);
    } else {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      canonical.setAttribute('href', finalUrl);
      document.head.appendChild(canonical);
    }
    
    // Handle product structured data
    if (product && product.name) {
      // Remove any existing product structured data
      const existingScript = document.querySelector('script[data-product-schema="true"]');
      if (existingScript) existingScript.remove();
      
      const productSchema = {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": String(product.name || ''),
        "description": String(product.description || '').substring(0, 500),
        "image": String(product.image_url || ''),
        "brand": {
          "@type": "Brand",
          "name": "Genora Peptides"
        },
        "offers": {
          "@type": "AggregateOffer",
          "priceCurrency": "USD",
          "lowPrice": product.variants ? Math.min(...product.variants.map(v => Number(v.price) || 0)) : Number(product.price) || 0,
          "highPrice": product.variants ? Math.max(...product.variants.map(v => Number(v.price) || 0)) : Number(product.price) || 0,
          "availability": product.variants?.some(v => v.in_stock) ? "https://schema.org/InStock" : "https://schema.org/OutOfStock"
        }
      };
      
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-product-schema', 'true');
      script.textContent = JSON.stringify(productSchema);
      document.head.appendChild(script);
    }
    
    // Cleanup function - remove product schema on unmount
    return () => {
      const productScript = document.querySelector('script[data-product-schema="true"]');
      if (productScript) productScript.remove();
    };
  }, [finalTitle, finalDescription, finalKeywords, finalImage, finalUrl, type, product, siteTitle]);

  // This component doesn't render anything visible
  return null;
};

export default SEO;
